import PropTypes from 'prop-types';
import React, { useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { addPageData, navigateWizard } from '../../actions';
import AutocompletePreload from '../../components/AutocompletePreload';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import SelectBase from '../../components/SelectBase';
import { PREVIOUS_EDUCATION } from '../../constants/heapEvents';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import useUniversities from '../../lib/hooks/useUniversities';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';
import { selectBaseDataSet } from '../../lib/selectHelpers';
import { customerGuidSelector, applicationIdSelector } from '../../reducers/selectors';

const levelsOfDegree = [
  'Bachelors',
  'Doctorate',
  'Masters',
  'Microcredentials',
  'Path-way',
  "Postgraduate Associate's Degree",
  'Postgraduate Certificate',
  'Postgraduate Diploma',
  "Undergraduate Associate's Degree",
  'Undergraduate Certificate',
  'Undergraduate Diploma',
];

const studyFields = [
  'Accounting',
  'Aerospace Engineering',
  'Analytics',
  'Bio/Biotech Engineering',
  'Biological Sciences',
  'Chemical Engineering',
  'Chemistry',
  'Civil & Environmental Engineering (Urban Systems and Transportation)',
  'Computer Science & Computer Engineering',
  'Dentistry',
  'Earth Sciences',
  'Electrical Engineering',
  'Engineering Management',
  'Finance',
  'Information Systems',
  'JD',
  'LLM',
  'Management',
  'Mathematics',
  'MBA',
  'Mechanical Engineering',
  'Nursing',
  'Operations Research',
  'Other Business',
  'Other Engineering',
  'Other Healthcare',
  'Other Humanities',
  'Other Law',
  'Other Science',
  'Physics',
  'Public Health',
  'Public Policy',
  'Robotics',
  'Vehicle Engineering',
];

const PreviousEducationPage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  previousEducationLevelOfDegree,
  previousEducationFieldOfStudy,
  previousEducationUniversity,
  customerGuid,
  applicationGuid,
}) => {
  const { data } = useUniversities();

  const initialUniversity = {
    value: previousEducationUniversity.guid,
    label: previousEducationUniversity.name,
  };
  const initialLevelOfDegree = {
    value: previousEducationLevelOfDegree,
    label: previousEducationLevelOfDegree,
  };
  const initialFieldOfStudy = {
    value: previousEducationFieldOfStudy,
    label: previousEducationFieldOfStudy,
  };

  const [selectedUniversity, setSelectedUniversity] = useState(initialUniversity);
  const [levelOfDegree, setSelectedLevelOfDegree] = useState(initialLevelOfDegree);
  const [fieldOfStudy, setSelectedFieldOfStudy] = useState(initialFieldOfStudy);

  useLayoutEffect(() => {
    track(PREVIOUS_EDUCATION, {
      'Customer GUID': customerGuid,
      'Application GUID': applicationGuid,
    });
  }, []);

  const handleSubmit = event => {
    event.preventDefault();

    analytics.sendEvent('Click - Next', 'previous-education');

    const universityNamePayload = payloadFactory.createAddPageDataPayload(
      'previousEducationUniversity',
      selectedUniversity
        ? { guid: selectedUniversity.value, name: selectedUniversity.label }
        : { guid: '', name: '' },
    );
    const levelOfDegreePayload = payloadFactory.createAddPageDataPayload(
      'previousEducationLevelOfDegree',
      levelOfDegree ? levelOfDegree.label : '',
      'previousEducationLevelOfDegree',
      levelOfDegree ? levelOfDegree.label : '',
    );
    const fieldOfStudyPayload = payloadFactory.createAddPageDataPayload(
      'previousEducationFieldOfStudy',
      fieldOfStudy ? fieldOfStudy.label : '',
      'previousEducationFieldOfStudy',
      fieldOfStudy ? fieldOfStudy.label : '',
    );

    dispatchAddPageData(universityNamePayload);
    dispatchAddPageData(levelOfDegreePayload);
    dispatchAddPageData(fieldOfStudyPayload);

    track('Submit - Previous Education page', {
      university_name: selectedUniversity ? selectedUniversity.label : '',
      level_of_degree: levelOfDegree ? levelOfDegree.label : '',
      field_of_study: fieldOfStudy ? fieldOfStudy.label : '',
    });

    dispatchWizardNav({ dir: 'fwd' });
  };

  const setUniversity = university => {
    track('Click - Previous Education - University name', {
      university_name: university,
    });
    setSelectedUniversity(university);
  };
  const setLevelOfDegree = level => {
    track('Click - Previous Education - Level of degree', {
      level_of_degree: level,
    });
    setSelectedLevelOfDegree(level);
  };

  const setFieldOfStudy = field => {
    track('Click - Previous Education - Field of study', {
      field_of_study: field,
    });
    setSelectedFieldOfStudy(field);
  };

  const handleApplyDefault = event => {
    event.preventDefault();

    const defaultUniversity = {
      guid: '',
      name: '',
    };

    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload('previousEducationUniversity', defaultUniversity),
    );
    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload('previousEducationLevelOfDegree', ''),
    );
    dispatchAddPageData(
      payloadFactory.createAddPageDataPayload('previousEducationFieldOfStudy', ''),
    );
    dispatchWizardNav({ dir: 'fwd', resetState: true });
  };

  return (
    <PageLayout
      title="What is the highest level of undergrad education you have completed or currently pursuing?"
      helpText="If you are applying for a loan for the current studies, please provide details of the education you most recently completed."
    >
      <form className="row-spacer">
        <div className="row">
          <div className="col col--12/16-large">
            <div className="row-spacer">
              <p className="text-field__input-header">College/University</p>
              <div className="row-spacer">
                <AutocompletePreload
                  data-testid="universities"
                  defaultOption={selectedUniversity}
                  onChange={setUniversity}
                  options={data}
                  placeholder="Search for your university"
                  noOptionsMessage="Enter your university name to continue."
                  loadingMessage="Loading data. Please wait..."
                  type="creatable"
                  disableFuzzyMatch={true}
                />
              </div>
            </div>

            <div className="row-spacer">
              <p className="text-field__input-header">Degree level</p>
              <div className="row-spacer">
                <SelectBase
                  data-testid="levelOfDegree"
                  selectedValue={levelOfDegree}
                  onChange={setLevelOfDegree}
                  options={selectBaseDataSet(levelsOfDegree)}
                  placeholder="eg. Postgraduate diploma"
                  noOptionsMessage="We couldn't find that degree level."
                  disableFuzzyMatch={false}
                />
              </div>
            </div>

            <div className="row-spacer">
              <p className="text-field__input-header">Field of study</p>
              <div className="row-spacer">
                <SelectBase
                  data-testid="fieldOfStudy"
                  selectedValue={fieldOfStudy}
                  onChange={setFieldOfStudy}
                  options={selectBaseDataSet(studyFields)}
                  placeholder="eg. Accounting"
                  noOptionsMessage="We couldn't find that field."
                  disableFuzzyMatch={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row-spacer">
          <Button type="submit" onClick={e => handleSubmit(e)}>
            Next
          </Button>
        </div>
      </form>
      <div className="row-spacer">
        <Link to="/work-experience" onClick={handleApplyDefault}>
          My college / university is not listed
        </Link>
      </div>
    </PageLayout>
  );
};

PreviousEducationPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  previousEducationFieldOfStudy: PropTypes.string.isRequired,
  previousEducationLevelOfDegree: PropTypes.string.isRequired,
  previousEducationUniversity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    guid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  customerGuid: PropTypes.string.isRequired,
  applicationGuid: PropTypes.string.isRequired,
};

export const mapStateToProps = state => ({
  previousEducationFieldOfStudy: state.builder.ui.previousEducationFieldOfStudy,
  previousEducationLevelOfDegree: state.builder.ui.previousEducationLevelOfDegree,
  previousEducationUniversity: state.builder.ui.previousEducationUniversity,
  customerGuid: customerGuidSelector(state),
  applicationGuid: applicationIdSelector(state),
});

PreviousEducationPage.defaultProps = {
  previousEducationUniversity: { name: '', guid: '' },
  previousEducationFieldOfStudy: '',
  previousEducationLevelOfDegree: '',
};

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { PreviousEducationPage };

export default connect(mapStateToProps, mapDispatchToProps)(PreviousEducationPage);
